import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Grid2, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import accountCheck from "@iconify/icons-mdi/account-check";
import { OcRoleFilterProps } from "./role-filter.types";
import { useOcRoleFilter } from "./use-role-filter";
import { MyHrRoleChip } from "my-hr/components";
import {
  OcCardButtonBar,
  OcRoleFilterGroupedSubtypes,
  OcRoleFilterHeader,
  OcRoleFilterSummary,
  OcRoleFilterSwitchItem,
} from "oc/components";
import { ProductsT } from "oc/configs/constants";

// defaultOpened gets through, but doesn't cause any issues.
type OcRoleFilterViewProps = OcRoleFilterProps &
  ReturnType<typeof useOcRoleFilter>;

const icons: Record<ProductsT, string> = {
  MyHR: "mdi:account",
  MyLeave: "mdi:bag-personal",
  MyCafeteria: "mdi:coffee",
  MyTime: "mdi:clock",
  MyTimesheet: "mdi:timer",
  MyDocuments: "mdi:text-box-multiple",
  MyNews: "mdi:newspaper-variant",
};

export const OcRoleFilterView: React.FC<OcRoleFilterViewProps> = ({
  buttonBarProps,
  //cardButtonBarPropsWithDefaults,
  chipData,
  error,
  examinersNames,
  editorsNames,
  assignersNames,
  handleOpenHeader,
  innerWidth,
  noCollapse,
  noFilters,
  OcButtonGroup,
  opened,
  subtypeProps,
  summaryItems,
  switchesDef,
  containerPadding,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Helper function to get role members list title translation key
  const getRoleMembersTitleKey = (product: string | undefined): string | null => {
    if (!product) return null;

    switch (product) {
      case "MyNews":
        return "OC_ROLE_EDITORS";
      case "MyCafeteria":
        return null;
      default:
        return "OC_ROLE_EXAMINERS";
    }
  };

  // Helper function to get assigners title translation key
  const getAssignersTitleKey = (product: string | undefined): string | null => {
    if (!product) return null;

    switch (product) {
      case "MyLeave":
        return "OC_ROLE_ASSIGNERS";
      default:
        return null;
    }
  };

  // Helper function to get missing role members message translation key
  const getMissingRoleMembersKey = (product: string | undefined): string | null => {
    if (!product) return null;

    switch (product) {
      case "MyNews":
        return "OC_MISSING_EDITORS";
      default:
        return "OC_MISSING_EXAMINERS";
    }
  };

  // Determine which names list to display and get title
  const roleMembersNames = editorsNames || examinersNames;
  const roleMembersTitleKey = getRoleMembersTitleKey(chipData?.product);
  const assignersTitleKey = getAssignersTitleKey(chipData?.product);

  return (
    <Box
      bgcolor={theme.palette.gray20.main}
      borderRadius="4px"
      border={error ? "1px solid red" : ""}
    >
      <OcRoleFilterHeader
        // If chipData is given, the icon and title will also change.
        opened={opened}
        handleOpen={handleOpenHeader}
        icon={chipData && icons[chipData.product as ProductsT]}
        noButton={noCollapse}
        title={chipData && chipData.product}
        titleExtra={
          chipData && (
            <Box>
              <MyHrRoleChip role={chipData.role} />
            </Box>
          )
        }
      />
      <Collapse
        in={opened}
        unmountOnExit
        sx={{
          padding: "0px 16px",
        }}
      >
        <Box height="2px" bgcolor={theme.palette.gray40.main} />
        <Box
          id="collapse-body"
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: containerPadding,
          }}
        >
          <Box width={innerWidth}>
            {OcButtonGroup && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingBottom: "32px",
                }}
              >
                <OcButtonGroup />
              </Box>
            )}
            {switchesDef && (
              <Grid2
                container
                spacing={2}
                sx={{ display: "flex", paddingBottom: "32px" }}
              >
                {switchesDef.map((switchDef) => (
                  <OcRoleFilterSwitchItem
                    key={switchDef.label}
                    {...switchDef}
                  />
                ))}
              </Grid2>
            )}
            {!noFilters && <OcRoleFilterGroupedSubtypes {...subtypeProps} />}
            {summaryItems && (
              <>
                <Box height="16px" />
                <OcRoleFilterSummary itemsDef={summaryItems} />
              </>
            )}
            {roleMembersTitleKey && chipData?.role === "user" && (
              <>
                <Box marginLeft="32px">
                  <Typography variant="body1" fontWeight={500} marginBottom="8px">
                    {t(roleMembersTitleKey)}
                  </Typography>
                  {roleMembersNames && roleMembersNames.length > 0 ? (
                    roleMembersNames.filter(Boolean).map((memberName, index) => (
                      <Box
                        key={`role-member-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Icon icon={accountCheck} height="24px" />
                        <Box width="10px" />
                        <Box paddingBottom="4px">{memberName}</Box>
                      </Box>
                    ))
                  ) : (
                    <Box style={{ color: theme.palette.primary.main }}>
                      {t(getMissingRoleMembersKey(chipData?.product) || "")}
                    </Box>
                  )}
                </Box>
              </>
            )}
            {assignersTitleKey && chipData?.role === "user" && (
              <>
                <Box height="16px" />
                <Box marginLeft="32px">
                  <Typography variant="body1" fontWeight={500} marginBottom="8px">
                    {t(assignersTitleKey)}
                  </Typography>
                  {assignersNames && assignersNames.length > 0 ? (
                    assignersNames.filter(Boolean).map((assignerName, index) => (
                      <Box
                        key={`assigner-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Icon icon={accountCheck} height="24px" />
                        <Box width="10px" />
                        <Box paddingBottom="4px">{assignerName}</Box>
                      </Box>
                    ))
                  ) : (
                    <Box style={{ color: theme.palette.primary.main }}>
                      {t("OC_MISSING_ASSIGNERS")}
                    </Box>
                  )}
                </Box>
              </>
            )}
            {buttonBarProps && (
              // If there is no buttonBarProps, there are also no handlers
              // set, so most likely this doesn't need to be rendered.
              <OcCardButtonBar {...buttonBarProps} />
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
